'use client';
import { useEffect } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import type { Tenant } from '@core/Entities/App/Tenants.entity';
import type { Locale } from '@core/Entities/Locale/Locale.entity';
import type { Vehicle } from '@core/Entities/Vehicle/Vehicle.entity';
import { Typography } from '@gds/Typography/Typography';
import { useModal } from '@modal/Hooks/UseModal';

import { calculationAtom } from '../../Atoms/Calculation.atom';
import { currentProductAtom } from '../../Atoms/CurrentProduct.atom';
import { finCalcDataFieldValueAtom } from '../../Atoms/FinCalcDataFieldValue.atom';
import { finCalcMessagesAtom } from '../../Atoms/FinCalcMessages.atom';
import { isCalculatorLoadingAtom } from '../../Atoms/IsCalculatorLoading.atom';
import { FieldGroup } from '../../Elements/FieldGroup/FieldGroup';
import type { FinanceHelpModalContent } from '../../Entities/FinanceHelpModal.entity';
import { Header } from '../Header/Header';
import { TermsAndConditions } from '../TermsAndConditions/TermsAndConditions';
import { AdditionalServices } from './AdditionalServices/AdditionalServices';
import { CalculatorFieldsBottom, CalculatorFieldsTop } from './Calculator.fields';
import { type CalculatorMessages, defaultCalculatorMessages } from './Calculator.messages';
import { CalculatorSummary } from './CalculatorSummary';
import { FinanceHelpModal } from './FinanceHelpModal/FinanceHelpModal';
import { useCalculator } from './Hooks/UseCalculator';
import { LoadingCalculatorContent } from './LoadingElements/LoadingCalculatorContent';
import { LoadingCalculatorOverlay } from './LoadingElements/LoadingCalculatorOverlay';
import { LoadingSummaryContainer } from './LoadingElements/LoadingSummaryContainer';
import { ProductToggle } from './ProductToggle/ProductToggle';

import styles from './Calculator.module.css';

export type CalculatorProps = {
  locale: Locale;
  strings: CalculatorMessages;
  financeMessages: FinanceHelpModalContent;
  vehicleId: string;
  vehicle: Vehicle;
  tenant: Tenant | undefined;
};

export const Calculator = ({
  strings,
  locale,
  vehicleId,
  financeMessages,
  vehicle,
  tenant,
}: CalculatorProps) => {
  useCalculator(vehicleId);
  const calculation = useAtomValue(calculationAtom);
  const setFieldData = useSetAtom(finCalcDataFieldValueAtom('isLoading'));
  const summaryStrings =
    locale === 'uk'
      ? { ...strings, retailCashPrice: defaultCalculatorMessages.retailCashPrice }
      : strings;

  const setFinCalcMessages = useSetAtom(finCalcMessagesAtom);
  setFinCalcMessages(strings ?? defaultCalculatorMessages);

  useEffect(() => {
    setFieldData(false);
  }, []);

  const product = useAtomValue(currentProductAtom);
  const isLoading = useAtomValue(isCalculatorLoadingAtom);
  const { openModal } = useModal();

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };
  const hasError = calculation === null || !!calculation?.errorCode;
  const hasCaptiveError = calculation?.errorCode === 'CAPTIVE_SERVER_ERROR';
  const isReady = !!calculation && !!product;
  const isInitialLoading = calculation === undefined;

  const handleOpenModal = () => {
    openModal(
      <FinanceHelpModal
        locale={locale}
        financeModalStrings={strings}
        financeMessages={financeMessages}
        vehicle={vehicle}
      />,
      {
        header: strings.financeModalHeader,
        modalClassName: styles.modalContainer,
        modalBodyClassName: styles.modalBody,
      },
    );
  };

  if (!vehicle?.pricing?.hasFinance) return null;

  return (
    <section id="financing-calculator" className={styles.calculatorWrapper}>
      <div className={styles.mainWrapper}>
        <div
          className={`${styles.mainOverlay} ${!!hasCaptiveError || hasError ? styles.visible : ''}`}
        >
          <LoadingCalculatorOverlay strings={strings} />
        </div>
        <form onSubmit={onSubmit} autoComplete="on" id="financing-calculator-form">
          <div className={styles.formContainer}>
            {isInitialLoading ? (
              <LoadingCalculatorContent strings={strings} />
            ) : (
              <section className={styles.content}>
                <Header strings={strings} handleHelpDialog={handleOpenModal} />
                {!hasError && (
                  <>
                    <ProductToggle strings={strings} />
                    {isReady && (
                      <>
                        <FieldGroup fields={CalculatorFieldsTop} />
                        <FieldGroup fields={CalculatorFieldsBottom} />
                      </>
                    )}
                    <AdditionalServices key={'additionalServices'} strings={strings} />
                  </>
                )}
              </section>
            )}

            <div className={styles.divider} />

            {hasError ? (
              <div className={styles.error}>
                <div>
                  <Typography variant="h5">{strings.errorTitle}</Typography>

                  <Typography variant="body3" Component="p">
                    {strings.errorBody}
                  </Typography>
                </div>
              </div>
            ) : isLoading ? (
              <LoadingSummaryContainer strings={strings} />
            ) : (
              <CalculatorSummary strings={summaryStrings} tenant={tenant} />
            )}
          </div>
        </form>
        <TermsAndConditions strings={strings} />
      </div>
    </section>
  );
};
Calculator.displayName = 'Calculator';
